import { useState } from "react";
import { promptDialog } from "@/shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { useCaps } from "../CapsProvider";
import { pickFile } from "../../../util/download";
import { Icon } from "../../kit";
import { CompactCheck, MiniBtn } from "../inspector/rows";
import { LibraryGrid, type LibItem } from "../library/LibraryGrid";
import { useManifest } from "../library/useManifest";

/**
 * Model stage props (ModelStagePlugin, v16 `addStageModel`) — drop extra props
 * (display stands, backdrops) into the scene alongside the jewelry model.
 * Stage props are excluded from auto-fit / export. Local files are inlined as
 * data: URLs — a blob: URL made in this (playground) origin can't be fetched
 * from the viewer iframe's different-origin document.
 *
 * Feedback matters more here than anywhere else in the panel set: the viewer
 * routes stage props through `loader.loadModel()` rather than the tracked
 * loader, so its own LoaderOverlay never appears for them. If this panel shows
 * nothing, a 40 MB FBX import looks exactly like a dead button.
 */
export function ModelStagePanel() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [busy, setBusy] = useState<null | "file" | "url">(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [added, setAdded] = useState<{ id: string; name: string }[]>([]);
  const [groundBelow, setGroundBelow] = useState(false);
  const [groundErr, setGroundErr] = useState<string | null>(null);
  // Curated stage props. No static manifest exists for these — they come only
  // from the preset library, so an unreachable API yields an empty grid rather
  // than a 404, and the upload controls below still work.
  const { items: stages, urlFor, thumbFor } = useManifest(null, "stage");

  const message = (e: unknown) =>
    e instanceof Error && e.message ? e.message : "Couldn't load that stage model.";

  const addFromUrl = async (url: string, name: string) => {
    // No catch here previously: a 404, an unsupported format or an unknown RPC
    // on an older viewer became an unhandled rejection and the panel looked
    // untouched, so the user believed the import had worked.
    try {
      const id = await r.addStageModel(url);
      setAdded((cur) => [...cur, { id, name }]);
    } catch (e) {
      setFailed(message(e));
    }
  };

  // Pre-v31 viewers have no addStageModelFile, so the file has to be base64'd
  // into a data: URL first. That read is itself multi-second on a big GLB and
  // can fail (unreadable file, quota), which used to be a permanent silent
  // no-op because the reader had no onerror handler.
  const addFromUrlFallback = (f: File) =>
    new Promise<void>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") void addFromUrl(reader.result, f.name).then(resolve);
        else {
          setFailed("Couldn't read that file.");
          resolve();
        }
      };
      reader.onerror = () => {
        setFailed("Couldn't read that file.");
        resolve();
      };
      reader.readAsDataURL(f);
    });

  // v31: addStageModelFile transfers the File directly over the RPC bridge
  // (mirrors addModelFile) — no more data-URL round trip needed.
  const addFromFile = async (f: File) => {
    if (!caps.v31) return addFromUrlFallback(f);
    try {
      const id = await r.addStageModelFile(f);
      setAdded((cur) => [...cur, { id, name: f.name }]);
    } catch (e) {
      setFailed(message(e));
    }
  };

  const pickLocal = async () => {
    setFailed(null);
    setBusy("file");
    try {
      // pickFile now settles when the load finishes, not when the dialog opens,
      // so this holds the spinner for the real duration of the import.
      await pickFile(".glb,.gltf,.obj,.fbx,.stl,.3dm", addFromFile);
    } finally {
      setBusy(null);
    }
  };

  const pickUrl = async () => {
    const u = await promptDialog({ title: "Stage model URL", label: "URL" });
    if (!u) return;
    setFailed(null);
    setBusy("url");
    try {
      await addFromUrl(u, u.split("/").pop() ?? u);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <span className="text-[12px] font-semibold text-ed-text-2">Model Stage</span>
      {stages.length > 0 ? (
        <LibraryGrid
          items={stages.map((sItem): LibItem => ({ id: sItem.id, label: sItem.label, thumb: thumbFor(sItem) }))}
          activeId={null}
          onSelect={(it) => {
            // Stage props are ADDITIVE — picking a second one keeps the first,
            // so no tile is ever "active". Returned so the grid holds its
            // spinner for the real import, which has no loader overlay of its own.
            const entry = stages.find((x) => x.id === it.id);
            if (!entry) return;
            setFailed(null);
            return addFromUrl(urlFor(entry), entry.label);
          }}
          view="card"
        />
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {/* MiniBtn (not a bare <button>) so the clicked control itself spins and
            both go disabled — the old markup could only change a label on the
            empty-state text, which disappears as soon as one prop is added. */}
        <MiniBtn icon="folder_open" busy={busy === "file"} disabled={busy === "url"} onClick={() => void pickLocal()}>
          Prompt Local File
        </MiniBtn>
        <MiniBtn icon="link" busy={busy === "url"} disabled={busy === "file"} onClick={() => void pickUrl()}>
          Prompt URL
        </MiniBtn>
      </div>
      {failed ? <span className="text-[11px] leading-snug text-ed-red">{failed}</span> : null}
      {added.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {added.map((m) => (
            <li key={m.id} className="flex items-center gap-1.5 rounded border border-ed-border bg-white px-2 py-1">
              <Icon name="deployed_code" size={14} className="shrink-0 text-ed-text-3" />
              <span className="truncate text-[12px] text-ed-text">{m.name}</span>
            </li>
          ))}
          {busy ? (
            <li className="flex items-center gap-1.5 px-2 py-1 text-[12px] text-ed-text-3">
              <Icon name="progress_activity" size={14} className="shrink-0 animate-spin" /> Loading…
            </li>
          ) : null}
        </ul>
      ) : (
        <span className="text-[11px] text-ed-text-3">
          {busy ? "Loading…" : "No stage props yet. Add a display stand or backdrop model."}
        </span>
      )}
      {/* Lived only on Plugins › Model Stage — a different tab from the panel
          that holds every sibling control. Optimistic, and rolled back rather
          than left claiming a setting the viewer never took. */}
      <div className="flex items-center justify-between gap-2 border-t border-ed-border pt-2">
        <span className="text-[12px] text-ed-text-2">Ground below model</span>
        <CompactCheck
          checked={groundBelow}
          onChange={(v) => {
            setGroundBelow(v);
            setGroundErr(null);
            const req = caps.v31 ? r.setStageGroundBelow(v) : r.setGroundShadowBake({ enabled: true });
            void req.catch((e: unknown) => {
              setGroundBelow(!v);
              setGroundErr(e instanceof Error ? e.message : "Ground below model failed.");
            });
          }}
        />
      </div>
      {groundErr ? <span className="text-[11px] leading-snug text-ed-red">{groundErr}</span> : null}
      <p className="text-[11px] leading-snug text-ed-text-3">
        Stage props compose a product shot; they are excluded from auto-fit and export.
      </p>
    </div>
  );
}
